import { axiosInstance } from './api';

export interface AdminUserSummary {
  id: number;
  username: string;
  email: string;
  is_superuser: boolean;
  is_staff: boolean;
  zone_count: number;
  /** Последний вход любым способом (ISO); null — не входил с 26.10.2026. */
  last_login: string | null;
}

export interface AdminUserZoneMembership {
  /** null when this row has no direct membership — access comes only from
   *  a group (VISITS-1, #175). There is nothing to PATCH/DELETE on it. */
  id: number | null;
  zone_id: number;
  zone_name: string;
  role: string;
  /** Group names that also (or only) grant this zone. Empty when the row
   *  is a plain direct grant with no group overlap. */
  inherited_from_groups: string[];
  /** true when this row has no direct membership row — edit the group
   *  instead of this row. */
  locked: boolean;
}

export interface AdminUserTelegramBinding {
  telegram_username: string;
  telegram_id: number | null;
  /** ``true`` once the user has actually logged in via the TG widget
   *  and telegram_id was backfilled; ``false`` while waiting for that
   *  first login. */
  verified: boolean;
}

export interface AdminUserDetail {
  id: number;
  username: string;
  email: string;
  is_superuser: boolean;
  is_staff: boolean;
  last_login?: string | null;
  zone_memberships: AdminUserZoneMembership[];
  telegram_binding: AdminUserTelegramBinding | null;
  user_groups?: { id: number; name: string }[];
}

export interface AdminUsersListResponse {
  count: number;
  limit: number;
  offset: number;
  results: AdminUserSummary[];
}

export const ADMIN_USERS_BASE = '/core/api/admin/users/';

export const listAdminUsers = (params: { q?: string; limit?: number; offset?: number } = {}) => {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.limit != null) search.set('limit', String(params.limit));
  if (params.offset != null) search.set('offset', String(params.offset));
  const qs = search.toString();
  const url = qs ? `${ADMIN_USERS_BASE}?${qs}` : ADMIN_USERS_BASE;
  return axiosInstance.get<AdminUsersListResponse>(url);
};

/** Создать пользователя (#1969). Без пароля — вход только через Telegram-привязку. */
export const createAdminUser = (payload: { username: string; email?: string; password?: string }) =>
  axiosInstance.post<AdminUserDetail>(ADMIN_USERS_BASE, payload);

export const getAdminUser = (userId: number | string) =>
  axiosInstance.get<AdminUserDetail>(`${ADMIN_USERS_BASE}${userId}/`);

export const patchAdminUser = (
  userId: number | string,
  payload: Partial<Pick<AdminUserDetail, 'email' | 'is_superuser' | 'is_staff'>>,
) => axiosInstance.patch<AdminUserDetail>(`${ADMIN_USERS_BASE}${userId}/`, payload);

export const adminSetUserPassword = (userId: number | string, newPassword: string) =>
  axiosInstance.post(`${ADMIN_USERS_BASE}${userId}/set_password/`, { new_password: newPassword });

export const grantUserZone = (userId: number | string, zoneId: number | string, role: string) =>
  axiosInstance.post<AdminUserZoneMembership>(`${ADMIN_USERS_BASE}${userId}/zones/`, {
    zone_id: zoneId,
    role,
  });

export const revokeUserZone = (userId: number | string, membershipId: number) =>
  axiosInstance.delete(`${ADMIN_USERS_BASE}${userId}/zones/${membershipId}/`);

export const updateUserZoneRole = (
  userId: number | string,
  membershipId: number,
  role: string,
) =>
  axiosInstance.patch<AdminUserZoneMembership>(
    `${ADMIN_USERS_BASE}${userId}/zones/${membershipId}/`,
    { role },
  );

export const setAdminUserTelegramBinding = (
  userId: number | string,
  telegramUsername: string,
) =>
  axiosInstance.put<AdminUserDetail>(
    `${ADMIN_USERS_BASE}${userId}/telegram-binding/`,
    { telegram_username: telegramUsername },
  );

export const clearAdminUserTelegramBinding = (userId: number | string) =>
  axiosInstance.delete(`${ADMIN_USERS_BASE}${userId}/telegram-binding/`);
